import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Check, X, CheckCheck, AlertTriangle, Clock, Filter,
  UserCheck, Users, TrendingUp, RefreshCw, Calendar,
  Shield, User
} from 'lucide-react';

export default function VerificationPage() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending_tl: 0, pending_mgr: 0, verified: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [rejectDialog, setRejectDialog] = useState({ open: false, entryId: null });
  const [rejectReason, setRejectReason] = useState('');
  const [allPendingSummary, setAllPendingSummary] = useState({ total: 0, byDate: [] });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const isManager = user.role === 'manager';

  // Fetch summary of ALL pending entries across all dates
  const fetchAllPendingSummary = useCallback(async () => {
    try {
      // Get unverified count to know total
      // Admin: use pending-verification endpoint (unverified-count always returns 0 for admin)
      let totalUnverified = 0;
      if (isAdmin) {
        const pendingRes = await axiosInstance.get('/pulse-entries/pending-verification');
        totalUnverified = Array.isArray(pendingRes.data) ? pendingRes.data.length : 0;
      } else {
        const countResponse = await axiosInstance.get('/pulse-entries/unverified-count');
        totalUnverified = countResponse.data.unverified_count;
      }

      if (totalUnverified === 0) {
        setAllPendingSummary({ total: 0, byDate: [] });
        return;
      }

      // Fetch recent dates to check for pending entries
      const today = new Date();
      const datesToCheck = [];
      for (let i = 0; i < 30; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        datesToCheck.push(date.toISOString().split('T')[0]);
      }

      // Check each date for pending entries
      const dateChecks = await Promise.all(
        datesToCheck.map(async (date) => {
          try {
            const response = await axiosInstance.get('/pulse-entries/verification-status', {
              params: { date, status: 'pending_tl' }
            });
            return {
              date,
              pending_tl: response.data.stats?.pending_tl || 0,
              pending_mgr: response.data.stats?.pending_mgr || 0
            };
          } catch {
            return { date, pending_tl: 0, pending_mgr: 0 };
          }
        })
      );

      // Filter to only dates with pending entries and sort by date (oldest first)
      const datesWithPending = dateChecks
        .filter(d => d.pending_tl > 0 || d.pending_mgr > 0)
        .sort((a, b) => a.date.localeCompare(b.date));

      setAllPendingSummary({
        total: totalUnverified,
        byDate: datesWithPending
      });
    } catch (error) {
      console.error('Failed to fetch pending summary:', error);
    }
  }, []);

  const fetchVerificationStatus = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date: selectedDate };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await axiosInstance.get('/pulse-entries/verification-status', { params });
      setEntries(response.data.entries || []);
      setStats(response.data.stats || { total: 0, pending_tl: 0, pending_mgr: 0, verified: 0 });
    } catch (error) {
      toast.error('Failed to load verification data');
      console.error('Verification fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, statusFilter]);

  useEffect(() => {
    fetchVerificationStatus();
    fetchAllPendingSummary();
  }, [fetchVerificationStatus, fetchAllPendingSummary]);

  // Auto-refresh every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchVerificationStatus();
      fetchAllPendingSummary();
    }, 120000);
    return () => clearInterval(interval);
  }, [fetchVerificationStatus, fetchAllPendingSummary]);

  const handleVerify = async (entryId) => {
    try {
      // Determine which verification to do based on user role
      const endpoint = isManager || isAdmin
        ? `/pulse-entries/${entryId}/verify-manager`
        : `/pulse-entries/${entryId}/verify-tl`;

      await axiosInstance.post(endpoint);
      toast.success('Entry verified');
      fetchVerificationStatus();
      fetchAllPendingSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify entry');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      await axiosInstance.post(`/pulse-entries/${rejectDialog.entryId}/reject`, { reason: rejectReason });
      toast.success('Entry rejected and TL notified');
      setRejectDialog({ open: false, entryId: null });
      setRejectReason('');
      fetchVerificationStatus();
      fetchAllPendingSummary();
    } catch (error) {
      toast.error('Failed to reject entry');
    }
  };

  const handleBulkVerify = async () => {
    if (selectedEntries.length === 0) {
      toast.error('No entries selected');
      return;
    }
    try {
      await axiosInstance.post('/pulse-entries/verify-bulk', selectedEntries);
      toast.success(`Verified ${selectedEntries.length} entries`);
      setSelectedEntries([]);
      fetchVerificationStatus();
      fetchAllPendingSummary();
    } catch (error) {
      toast.error('Failed to verify entries');
    }
  };

  const toggleSelectEntry = (entryId) => {
    setSelectedEntries(prev =>
      prev.includes(entryId)
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const toggleSelectAll = () => {
    const selectableEntries = entries.filter(e => canVerify(e));
    if (selectedEntries.length === selectableEntries.length && selectableEntries.length > 0) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(selectableEntries.map(e => e.id));
    }
  };

  // Determine if current user can verify this entry
  const canVerify = (entry) => {
    if (isAdmin || isManager) {
      // Managers/Admins can verify entries that are TL-verified but not yet manager-verified
      return entry.tl_verified && !entry.mgr_verified;
    }
    // TLs can verify their own unverified entries
    return !entry.tl_verified;
  };

  // Get verification status display
  const getVerificationStatus = (entry) => {
    if (entry.mgr_verified) {
      return {
        label: 'Fully Verified',
        color: 'bg-green-500/10 text-green-600 border-green-500/20',
        icon: CheckCheck
      };
    }
    if (entry.tl_verified) {
      return {
        label: 'Pending Manager',
        color: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
        icon: Clock
      };
    }
    return {
      label: 'Pending TL',
      color: 'bg-red-500/10 text-red-600 border-red-500/20',
      icon: AlertTriangle
    };
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Group entries by gym
  const groupedEntries = entries.reduce((acc, entry) => {
    if (!acc[entry.gym_id]) {
      acc[entry.gym_id] = {
        gym_name: entry.gym_name,
        tl_name: entry.tl_name,
        manager_name: entry.manager_name,
        entries: []
      };
    }
    acc[entry.gym_id].entries.push(entry);
    return acc;
  }, {});

  // Calculate verification rate
  const verificationRate = stats.total > 0
    ? Math.round((stats.verified / stats.total) * 100)
    : 0;

  const uniqueGyms = Object.keys(groupedEntries).length;

  return (
    <div data-testid="verification-page">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <CheckCheck className="w-8 h-8 text-primary" />
          <div>
            <h1 className="page-title">VERIFICATION DASHBOARD</h1>
            <p className="page-subtitle">Track and manage all verification statuses</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchVerificationStatus}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ALL Pending Entries Summary - Show if there are unverified entries */}
      {allPendingSummary.total > 0 && (
        <Card className="mb-6 border-amber-500 bg-amber-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-amber-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {allPendingSummary.total} Unverified {allPendingSummary.total === 1 ? 'Entry' : 'Entries'} Require Verification
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-amber-800 text-sm mb-3">
              You have unverified pulse entries across {allPendingSummary.byDate.length} {allPendingSummary.byDate.length === 1 ? 'date' : 'dates'}.
              Click on a date below to view and verify those entries:
            </p>
            <div className="flex flex-wrap gap-2">
              {allPendingSummary.byDate.map(({ date, pending_tl, pending_mgr }) => (
                <Button
                  key={date}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedDate(date);
                    setStatusFilter('all');
                  }}
                  className={`border-amber-500 hover:bg-amber-100 ${selectedDate === date ? 'bg-amber-200' : ''}`}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {new Date(date + 'T00:00:00').toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                  <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                    {pending_tl}
                  </span>
                  {pending_mgr > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-full">
                      {pending_mgr}
                    </span>
                  )}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Entries</p>
                <p className="text-2xl font-bold text-blue-500">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Pending TL</p>
                <p className="text-2xl font-bold text-red-500">{stats.pending_tl}</p>
              </div>
              <User className="w-8 h-8 text-red-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Pending Manager</p>
                <p className="text-2xl font-bold text-amber-500">{stats.pending_mgr}</p>
              </div>
              <Shield className="w-8 h-8 text-amber-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Fully Verified</p>
                <p className="text-2xl font-bold text-green-500">{stats.verified}</p>
              </div>
              <CheckCheck className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Verification Rate</p>
                <p className="text-2xl font-bold text-purple-500">{verificationRate}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end justify-between">
            <div className="flex gap-4 items-end flex-wrap">
              {/* Date Filter */}
              <div className="space-y-2 w-48">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date
                </Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  data-testid="date-filter"
                />
              </div>

              {/* Status Tabs */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Status
                </Label>
                <Tabs value={statusFilter} onValueChange={setStatusFilter}>
                  <TabsList>
                    <TabsTrigger value="all" data-testid="tab-all">
                      All ({stats.total})
                    </TabsTrigger>
                    <TabsTrigger value="pending_tl" data-testid="tab-pending-tl">
                      Pending TL ({stats.pending_tl})
                    </TabsTrigger>
                    <TabsTrigger value="pending_mgr" data-testid="tab-pending-mgr">
                      Pending Mgr ({stats.pending_mgr})
                    </TabsTrigger>
                    <TabsTrigger value="verified" data-testid="tab-verified">
                      Verified ({stats.verified})
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>

            {/* Bulk Actions */}
            {(isManager || isAdmin) && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={toggleSelectAll}
                  disabled={entries.filter(e => canVerify(e)).length === 0}
                  data-testid="select-all-btn"
                >
                  {selectedEntries.length === entries.filter(e => canVerify(e)).length && selectedEntries.length > 0
                    ? 'Deselect All'
                    : 'Select All Pending'}
                </Button>
                <Button
                  onClick={handleBulkVerify}
                  disabled={selectedEntries.length === 0}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="bulk-verify-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Verify Selected ({selectedEntries.length})
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Entries List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCheck className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {statusFilter === 'all' ? 'No Entries Found' : 'All Caught Up!'}
            </h3>
            <p className="text-muted-foreground">
              {statusFilter === 'all'
                ? `No entries for ${selectedDate}`
                : `No ${statusFilter.replace('_', ' ')} entries for ${selectedDate}`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Summary by Gym Count */}
          <p className="text-sm text-muted-foreground">
            Showing {entries.length} entries across {uniqueGyms} gyms
          </p>

          {Object.entries(groupedEntries).map(([gymId, gymData]) => (
            <Card key={gymId} className="overflow-hidden">
              <CardHeader className="bg-muted/30 py-4">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-lg">{gymData.gym_name}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      TL: {gymData.tl_name} • Manager: {gymData.manager_name || 'N/A'} • {gymData.entries.length} entries
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {/* Gym-level stats */}
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-500/10 text-green-600">
                      <CheckCheck className="w-3 h-3 mr-1" />
                      {gymData.entries.filter(e => e.mgr_verified).length} verified
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-amber-500/10 text-amber-600">
                      <Clock className="w-3 h-3 mr-1" />
                      {gymData.entries.filter(e => e.tl_verified && !e.mgr_verified).length} pending mgr
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-500/10 text-red-600">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {gymData.entries.filter(e => !e.tl_verified).length} pending TL
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="data-table">
                  <table>
                    <thead>
                      <tr>
                        {(isManager || isAdmin) && (
                          <th className="w-12">
                            <input
                              type="checkbox"
                              checked={gymData.entries.filter(e => canVerify(e)).every(e => selectedEntries.includes(e.id)) && gymData.entries.filter(e => canVerify(e)).length > 0}
                              onChange={() => {
                                const selectableInGym = gymData.entries.filter(e => canVerify(e));
                                const allSelected = selectableInGym.every(e => selectedEntries.includes(e.id));
                                if (allSelected) {
                                  setSelectedEntries(prev => prev.filter(id => !selectableInGym.find(e => e.id === id)));
                                } else {
                                  setSelectedEntries(prev => [...new Set([...prev, ...selectableInGym.map(e => e.id)])]);
                                }
                              }}
                              className="w-4 h-4"
                              disabled={gymData.entries.filter(e => canVerify(e)).length === 0}
                            />
                          </th>
                        )}
                        <th>Pulse</th>
                        <th>Source</th>
                        <th>Dialed</th>
                        <th>Connected</th>
                        <th>Closures</th>
                        <th>Status</th>
                        <th>TL Verified</th>
                        <th>Mgr Verified</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gymData.entries.map((entry) => {
                        const status = getVerificationStatus(entry);
                        const StatusIcon = status.icon;
                        const canVerifyEntry = canVerify(entry);

                        return (
                          <tr
                            key={entry.id}
                            className={selectedEntries.includes(entry.id) ? 'bg-primary/5' : ''}
                            data-testid={`entry-row-${entry.id}`}
                          >
                            {(isManager || isAdmin) && (
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedEntries.includes(entry.id)}
                                  onChange={() => toggleSelectEntry(entry.id)}
                                  className="w-4 h-4"
                                  disabled={!canVerifyEntry}
                                />
                              </td>
                            )}
                            <td>{entry.pulse_name}</td>
                            <td>{entry.source_name}</td>
                            <td>{entry.dialed}</td>
                            <td>{entry.connected}</td>
                            <td className="font-semibold">{entry.total_closure}</td>
                            <td>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${status.color}`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {status.label}
                              </span>
                            </td>
                            <td>
                              {entry.tl_verified ? (
                                <div className="text-xs">
                                  <div className="flex items-center text-green-600">
                                    <UserCheck className="w-3 h-3 mr-1" />
                                    {entry.tl_name}
                                  </div>
                                  <div className="text-muted-foreground">
                                    {formatTimestamp(entry.tl_verified_at)}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-xs text-red-500">Not verified</span>
                              )}
                            </td>
                            <td>
                              {entry.mgr_verified ? (
                                <div className="text-xs">
                                  <div className="flex items-center text-green-600">
                                    <Shield className="w-3 h-3 mr-1" />
                                    {entry.mgr_verified_by || 'Manager'}
                                  </div>
                                  <div className="text-muted-foreground">
                                    {formatTimestamp(entry.mgr_verified_at)}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-xs text-amber-500">
                                  {entry.tl_verified ? 'Awaiting' : '-'}
                                </span>
                              )}
                            </td>
                            <td>
                              {canVerifyEntry ? (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 h-8"
                                    onClick={() => handleVerify(entry.id)}
                                    data-testid={`verify-btn-${entry.id}`}
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  {(isManager || isAdmin) && (
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      className="h-8"
                                      onClick={() => setRejectDialog({ open: true, entryId: entry.id })}
                                      data-testid={`reject-btn-${entry.id}`}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  {entry.mgr_verified ? 'Done' : (isManager || isAdmin) ? 'Awaiting TL' : 'Verified'}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ open, entryId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Entry</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason for Rejection</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection (e.g., incorrect data, missing values, needs correction)"
              className="mt-2"
              rows={4}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, entryId: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-btn">
              Reject Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
